/**
 * placementZoneToViewCode -- data-driven placement-zone <-> Artifi view-code
 * resolution.
 *
 * A SKU's real Artifi views ARE its placement zones (design doc
 * ``docs/planning/2026-07-22-data-driven-placement-zones.md``). A placement
 * persists a stable zone slug (the SLUGIFIED Artifi view code, e.g.
 * ``cap_front_panel``); the render/preview path resolves that slug BACK to
 * the SKU's live view code at draw time via ``liveViewCodeForSlug``. There is
 * no per-product hardcoded map -- the live ``skuViews`` payload (from GET
 * /api/artifi/product_views) is the sole authority.
 *
 * Phase 3 (GH #2224) removed the legacy ``PLACEMENT_ZONE_TO_VIEW_CODE`` map +
 * the SKU-18000 static constants (``DEFAULT_VIEW_CODE``, ``FRONT_VIEW_CODE``,
 * ``BACK_VIEW_CODE``, ``FRONT_GROUP_VIEW_CODES``, ``BACK_GROUP_VIEW_CODES``)
 * once Phases 1-2 proved every consumer resolves off live views.
 *
 * FAIL-OPEN CONTRACT (unchanged): when ``skuViews`` is empty/unresolved
 * (fetch not done, fetch error, Artifi creds missing) there is NO authority.
 * The resolvers degrade conservatively -- they do NOT hard-block a placement
 * and do NOT fabricate a static view code. The render path shows the bare
 * garment / hides the overlay, the wizard fails open (offers every zone), and
 * the backend substitution net (``artifi_preview.py``) is the last guard.
 *
 * This mirrors ``api/src/services/placement_zone_view_code.py`` (the
 * ``slugify_view_code`` + ``LEGACY_VIEWCODE_SLUG_ALIASES`` + per-view default
 * dims). Keep the two in sync -- a divergence would make the frontend resolve
 * a slug to a view code the backend never resolved it from (or vice versa).
 *
 * Aliases beyond the one static legacy entry live in the database
 * (``placement_zone_view_aliases``, three-way placement map 2026-10-29); the
 * frontend receives them pre-resolved as each live view's ``zone_slug``, and
 * the backend's ``live_view_code_for_slug(..., aliases=)`` reads the table.
 */

function _isFrontGroup(groupName) {
    if (typeof groupName !== "string") return false;
    return groupName.toLowerCase().includes("front");
}

// ---------------------------------------------------------------------------
// Data-driven zone identity (Phase 1/2, GH #2214 / #2217).
//
// The canonical PlacementZone slug is the SLUGIFIED Artifi view code --
// ``slugifyViewCode("Cap Front Panel") == "cap_front_panel"``. The wizard
// sends the chosen view code and the backend find-or-creates a zone keyed on
// that slug. The render path resolves a placement's persisted zone slug BACK
// to a view code by matching the slugified form of one of the SKU's actual
// view codes (the reverse of the backend ``view_code_to_zone_slug``).
//
// This mirrors ``api/src/services/placement_zone_view_code.py``
// (``slugify_view_code`` + ``LEGACY_VIEWCODE_SLUG_ALIASES``). Keep the two in
// sync.
// ---------------------------------------------------------------------------

// ONE bounded legacy-compat alias (mirrors the backend). "Center Back"
// slugifies to "center_back", but the seeded apparel catalog uses "full_back"
// for that location; existing back placements persist ``full_back``, so a
// ``full_back`` slug must resolve back to the "Center Back" view code.
const _LEGACY_SLUG_TO_VIEW_CODE = Object.freeze({
    full_back: "Center Back",
});

// Per-view default imprint envelope (inches) for a FRESHLY-MINTED zone
// (Phase 2, GH #2217). In data-driven view-code mode the wizard has no
// catalog row to read default dims from (the zone is find-or-created
// server-side on submit), so the dimensions step would land blank. This
// supplies a sensible per-view default -- a cap front panel mirrors the
// seeded hat_front 4x2.5, side panels mirror hat_side 2.5x2.5, etc. MUST
// stay in sync with the backend ``default_dims_for_view_code`` in
// ``api/src/services/placement_zone_view_code.py`` (which stamps the same
// dims onto the minted zone) so the wizard prefill matches what persists.
// This is a PER-VIEW heuristic, NOT a per-product apparel map.
const _DEFAULT_DIMS_GENERIC = Object.freeze({ widthIn: 4.0, heightIn: 4.0 });

/**
 * A sensible default imprint envelope (inches) for a view, keyed on words in
 * the view code. Mirrors the backend ``default_dims_for_view_code``. Never
 * throws; a non-string / unrecognized code returns the generic 4x4.
 *
 * @param {string} viewCode  e.g. "Cap Front Panel", "Left Sleeve"
 * @returns {{widthIn: number, heightIn: number}}
 */
export function defaultDimsForViewCode(viewCode) {
    if (typeof viewCode !== "string") return { ..._DEFAULT_DIMS_GENERIC };
    const lower = viewCode.toLowerCase();
    if (lower.includes("sleeve")) return { widthIn: 3.0, heightIn: 12.0 };
    if (lower.includes("upper back") || lower.includes("upper_back")) {
        return { widthIn: 10.0, heightIn: 4.0 };
    }
    if (_looksBack(lower)) return { widthIn: 12.0, heightIn: 14.0 };
    if (lower.includes("cap") || lower.includes("hat")) {
        if (
            lower.includes("side") ||
            lower.includes("left") ||
            lower.includes("right")
        ) {
            return { widthIn: 2.5, heightIn: 2.5 };
        }
        return { widthIn: 4.0, heightIn: 2.5 };
    }
    return { ..._DEFAULT_DIMS_GENERIC };
}

/**
 * Slugify an Artifi view code to its stable zone slug. Deterministic and
 * idempotent: lowercase, every run of non-alphanumerics collapses to one
 * underscore, leading/trailing underscores stripped. Mirrors the backend
 * ``slugify_view_code`` byte-for-byte in behavior.
 *
 * @param {string} viewCode
 * @returns {string}  the slug, or "" for non-string / empty / punctuation-only
 */
export function slugifyViewCode(viewCode) {
    if (typeof viewCode !== "string") return "";
    const out = [];
    let prevUnderscore = false;
    for (const ch of viewCode.toLowerCase()) {
        if (/[a-z0-9]/.test(ch)) {
            out.push(ch);
            prevUnderscore = false;
        } else if (!prevUnderscore) {
            out.push("_");
            prevUnderscore = true;
        }
    }
    return out.join("").replace(/^_+|_+$/g, "");
}

/**
 * The zone slug a view code resolves to: the legacy alias ("Center Back" ->
 * "full_back"), else the slugified code. Mirrors the backend
 * ``view_code_to_zone_slug`` -- the zone row the bind finds (or mints) for
 * that view, whose envelope sizes a new placement there.
 *
 * @param {string} viewCode
 * @returns {string}  the slug, or "" for non-string / empty / punctuation-only
 */
export function viewCodeToZoneSlug(viewCode) {
    if (typeof viewCode !== "string") return "";
    for (const [slug, code] of Object.entries(_LEGACY_SLUG_TO_VIEW_CODE)) {
        if (code === viewCode) return slug;
    }
    return slugifyViewCode(viewCode);
}

/**
 * Resolve a persisted zone slug to the SKU's live view code that slugifies to
 * it (the reverse of the backend ``view_code_to_zone_slug``). Returns the
 * live view ``code`` (the value Artifi GetImage accepts) when a match exists,
 * else ``null``. The legacy alias (``full_back`` -> "Center Back") is honored
 * so old apparel placements still resolve to their real view.
 *
 * Matches against each live view's ``code`` first, then its ``display_name``
 * (some SKUs return a SKU-prefixed ``code`` with a bare ``display_name`` --
 * the 637167 empty-picker shape) -- always returning the render ``code``.
 *
 * @param {string|null} zoneSlug   e.g. "cap_front_panel", "left_chest"
 * @param {Array<{code?: string, display_name?: string}>|null|undefined} skuViews
 * @returns {string|null}  the live view code, or null when no live view maps
 */
export function liveViewCodeForSlug(zoneSlug, skuViews) {
    if (zoneSlug == null || !Array.isArray(skuViews)) return null;
    const wanted = slugifyViewCode(String(zoneSlug));
    if (!wanted) return null;
    // Three-way placement map (2026-10-29): GET /api/artifi/product_views
    // names, per view, the curated zone a bind of it lands on (``zone_slug``),
    // resolved server-side through the Artifi alias table. That is the only
    // way to find an ALIASED zone's view ("Front Front Center Center" ->
    // front_center_center), so it is consulted first; the slug matching
    // below still covers every un-aliased zone and older payloads.
    for (const v of skuViews) {
        if (v && typeof v.zone_slug === "string" && v.zone_slug === wanted) {
            const code = typeof v.code === "string" ? v.code : null;
            const display =
                typeof v.display_name === "string" ? v.display_name : null;
            if (code || display) return code || display;
        }
    }
    const legacyViewCode = _LEGACY_SLUG_TO_VIEW_CODE[wanted];
    for (const v of skuViews) {
        if (!v) continue;
        const code = typeof v.code === "string" ? v.code : null;
        const display =
            typeof v.display_name === "string" ? v.display_name : null;
        // A legacy-aliased slug matches the aliased view code directly.
        if (legacyViewCode) {
            if (code === legacyViewCode || display === legacyViewCode) {
                return code || legacyViewCode;
            }
        }
        if (code && slugifyViewCode(code) === wanted) return code;
        if (display && slugifyViewCode(display) === wanted) return code || display;
    }
    return null;
}

/**
 * Return the code strings actually configured for a SKU.
 * @param {Array<{code: string}>|null|undefined} skuViews
 * @returns {string[]}
 */
function _viewCodes(skuViews) {
    if (!Array.isArray(skuViews)) return [];
    return skuViews
        .map((v) => (v && typeof v.code === "string" ? v.code : null))
        .filter((c) => c != null);
}

/**
 * The set of strings a slug may match against for a SKU -- BOTH the render
 * ``code`` AND the human ``display_name``.
 *
 * Some SKUs (e.g. 637167) return a Template-shaped GetProductViews payload
 * whose ``code`` is SKU-prefixed ("637167_Left Chest") while ``display_name``
 * is the bare label ("Left Chest"). Including ``display_name`` here makes a
 * match succeed whether ``code`` is bare (post backend-fix) or still prefixed
 * (belt-and-suspenders), without disturbing the front/back camera selection
 * that legitimately needs the real ``code`` (see ``_viewCodes`` /
 * ``frontViewForSku``).
 *
 * @param {Array<{code?: string, display_name?: string}>|null|undefined} skuViews
 * @returns {string[]}
 */
function _matchCodes(skuViews) {
    if (!Array.isArray(skuViews)) return [];
    const out = [];
    for (const v of skuViews) {
        if (!v) continue;
        if (typeof v.code === "string") out.push(v.code);
        if (typeof v.display_name === "string") out.push(v.display_name);
    }
    return out;
}

/**
 * The default view code for a SKU when no placement is selected.
 *
 * Prefers the SKU's first FRONT-group view (the garment front is the
 * natural default preview). Falls back to the first configured view if no
 * group looks like a front group. Returns ``null`` when the SKU views are
 * unavailable -- there is no authority to name a view and no static default
 * to fall back on (Phase 3: the legacy "Full Center" default is gone).
 * Callers treat a null selected view as "no specific camera" and render
 * conservatively.
 *
 * @param {Array<{code: string, group?: string}>|null|undefined} skuViews
 * @returns {string|null}
 */
export function defaultViewForSku(skuViews) {
    if (!Array.isArray(skuViews) || skuViews.length === 0) {
        return null;
    }
    // The product_views endpoint emits BOTH the group-level entry (e.g.
    // code "Front", group "Front") AND the nested per-camera entries
    // (e.g. code "Left Chest", group "Front"). A group code is NOT a
    // render-able camera -- Artifi 500s on it. Prefer entries whose code
    // differs from their group (real cameras) over the group header.
    const isCamera = (v) =>
        v && typeof v.code === "string" && v.code !== v.group;

    // 1. First FRONT-group camera.
    const frontCamera = skuViews.find(
        (v) => isCamera(v) && _isFrontGroup(v.group),
    );
    if (frontCamera) return frontCamera.code;
    // 1b. group absent -> first camera whose code doesn't look back-of-
    //     garment (deterministic front default when group is unavailable;
    //     mirrors frontViewForSku so the no-placement preview never lands
    //     on the back when Artifi returns a back view first with group=None).
    const frontByCode = skuViews.find(
        (v) => isCamera(v) && v.group == null && !_looksBack(v.code),
    );
    if (frontByCode) return frontByCode.code;
    // 2. First camera in any group.
    const anyCamera = skuViews.find((v) => isCamera(v));
    if (anyCamera) return anyCamera.code;
    // 3. Any entry with a code (SKU exposes only group-level views).
    const first = skuViews.find(
        (v) => v && typeof v.code === "string",
    );
    return first ? first.code : null;
}

/**
 * Resolve a placement zone slug to a view code that EXISTS for the given SKU.
 *
 * Resolution order:
 *   1. No SKU views available (no authority) -> ``null`` (Phase 3: there is
 *      no static map to fall back to; the caller renders conservatively and
 *      the backend substitution net is the last guard).
 *   2. zoneCode is null (no placement selected) -> defaultViewForSku.
 *   3. The slug is the slugified form of one of the SKU's LIVE view codes
 *      (cap_front_panel -> "Cap Front Panel", left_chest -> "Left Chest",
 *      full_back -> "Center Back" via the legacy alias) -> that live code.
 *   4. The slug matches no live view -> the SKU's default front view. Never
 *      pass a code the SKU lacks to Artifi.
 *
 * @param {object} args
 * @param {string|null} args.zoneCode
 * @param {Array<{code: string, group?: string}>|null|undefined} args.skuViews
 * @returns {string|null}
 */
export function resolveViewCodeForSku({ zoneCode, skuViews } = {}) {
    const codes = _viewCodes(skuViews);
    if (codes.length === 0) {
        // No live views -> no authority, no static fallback.
        return null;
    }
    if (zoneCode == null) {
        return defaultViewForSku(skuViews);
    }
    // Data-driven (#2217): resolve the slug as the slugified form of one of
    // the SKU's live view codes (cap_front_panel -> "Cap Front Panel"), so a
    // view-driven zone renders on its OWN view rather than the front default.
    const liveCode = liveViewCodeForSlug(zoneCode, skuViews);
    if (liveCode != null) return liveCode;
    // The slug is not a real view for this SKU -- fall back to a configured
    // front view rather than passing a bad code through.
    return defaultViewForSku(skuViews);
}

// ---------------------------------------------------------------------------
// AUTHORITATIVE-view helpers: the live Artifi views are the source of truth so
// the UI never OFFERS an invalid placement and never SENDS Artifi a view a
// product lacks.
//
// Contract for both helpers below:
//   * When live ``skuViews`` are available, they are AUTHORITATIVE -- a zone
//     whose slug is not the slugified form of one of the SKU's view codes is
//     INVALID for that product.
//   * When ``skuViews`` are unavailable (not fetched yet, fetch error, or
//     Artifi creds missing), we FAIL OPEN: treat every zone as valid so a
//     transient fetch miss never blanks the picker. The backend substitution
//     net is the last guard.
// ---------------------------------------------------------------------------

/**
 * Is this placement zone valid for the given SKU? Used to FILTER the zone
 * picker so invalid placements are never offered.
 *
 * @param {string|null} zoneCode  the zone slug (e.g. "left_chest")
 * @param {Array<{code: string, display_name?: string}>|null|undefined} skuViews
 * @returns {boolean}  true if valid (or if views are unavailable -> fail open)
 */
export function isZoneValidForSku(zoneCode, skuViews) {
    // Authority is present when the SKU exposes ANY view.
    const codes = _matchCodes(skuViews);
    if (codes.length === 0) return true; // fail open -- no authority to filter
    if (zoneCode == null) return false;
    // Data-driven: the slug IS the slugified live view code for a minted zone
    // (cap_front_panel -> "Cap Front Panel"). Valid iff it maps to a live view.
    return liveViewCodeForSlug(zoneCode, skuViews) != null;
}

/**
 * Strict resolver: the REAL Artifi view code for a zone on a SKU, or ``null``
 * when the zone has no valid view for the product (the hard-block signal).
 *
 * Unlike ``resolveViewCodeForSku`` (which substitutes a default so the Stage
 * preview always shows *something*), this NEVER substitutes -- callers on the
 * write/overlay path use ``null`` to hide/block an invalid placement.
 *
 * With NO authority (skuViews unavailable) it FAILS OPEN by returning the
 * zone slug unchanged (a non-null, best-effort passthrough) rather than
 * blocking -- the backend substitution net validates it on write.
 *
 * @param {string|null} zoneCode
 * @param {Array<{code: string, display_name?: string}>|null|undefined} skuViews
 * @returns {string|null}  the exact view code, null if invalid-with-authority,
 *   or the raw zone code when there is no authority (fail open)
 */
export function strictViewCodeForSku(zoneCode, skuViews) {
    // Authority present when the SKU exposes ANY view.
    const matchable = _matchCodes(skuViews);
    if (matchable.length === 0) {
        // No authority: fail OPEN with a best-effort passthrough (never block).
        return zoneCode == null ? null : String(zoneCode);
    }
    if (zoneCode == null) return null;
    // Data-driven: resolve the slug as the slugified form of one of the SKU's
    // live view codes. Returns the live render ``code`` -- exactly what Artifi
    // GetImage accepts -- or null when the zone is invalid for this SKU.
    return liveViewCodeForSlug(zoneCode, skuViews);
}

// Codes that name a rear-of-garment view, used to classify front/back when
// Artifi's ``group`` field is absent (K540 returns group=None for all views
// -- an upstream-shape gap). Substring match, case-insensitive.
const _BACK_CODE_HINTS = ['back', 'rear', 'nape'];

function _looksBack(code) {
    if (typeof code !== 'string') return false;
    const lower = code.toLowerCase();
    return _BACK_CODE_HINTS.some((h) => lower.includes(h));
}

/**
 * The SKU's real FRONT-group default view code (for the StageHeader Front
 * pill). Prefers a camera whose group looks like "front"; when ``group`` is
 * missing (upstream gap), prefers the first view whose CODE doesn't look
 * back-of-garment. Returns ``null`` when no live views are available (Phase 3:
 * the static FRONT_VIEW_CODE default is gone).
 *
 * @param {Array<{code: string, group?: string}>|null|undefined} skuViews
 * @returns {string|null}
 */
export function frontViewForSku(skuViews) {
    if (!Array.isArray(skuViews) || skuViews.length === 0) {
        return null;
    }
    const isCamera = (v) =>
        v && typeof v.code === 'string' && v.code !== v.group;
    const byGroup = skuViews.find(
        (v) => isCamera(v) && _isFrontGroup(v.group),
    );
    if (byGroup) return byGroup.code;
    // group absent -> first camera whose code isn't a back view.
    const byCode = skuViews.find((v) => isCamera(v) && !_looksBack(v.code));
    if (byCode) return byCode.code;
    return defaultViewForSku(skuViews);
}

/**
 * The SKU's real BACK-group default view code (for the StageHeader Back
 * pill). Symmetric to frontViewForSku. Returns null when the SKU has NO
 * back view at all (so the Back pill can be hidden/disabled) OR when no live
 * views are available (Phase 3: the static BACK_VIEW_CODE default is gone).
 *
 * @param {Array<{code: string, group?: string}>|null|undefined} skuViews
 * @returns {string|null}
 */
export function backViewForSku(skuViews) {
    if (!Array.isArray(skuViews) || skuViews.length === 0) {
        return null;
    }
    const isCamera = (v) =>
        v && typeof v.code === 'string' && v.code !== v.group;
    const byGroup = skuViews.find(
        (v) => isCamera(v) && !_isFrontGroup(v.group) && v.group != null,
    );
    if (byGroup) return byGroup.code;
    const byCode = skuViews.find((v) => isCamera(v) && _looksBack(v.code));
    return byCode ? byCode.code : null;
}

/**
 * The set of view codes for a SKU's front-of-garment group (for pill
 * activation). Uses the live group when present, else code-name heuristics.
 * Returns an EMPTY set when no live views are available (Phase 3: the static
 * FRONT_GROUP_VIEW_CODES fallback is gone -- with no authority, no view
 * classifies, matching the fail-open "no group" behavior downstream).
 *
 * @param {Array<{code: string, group?: string}>|null|undefined} skuViews
 * @returns {Set<string>}
 */
export function frontGroupCodesForSku(skuViews) {
    const out = new Set();
    if (!Array.isArray(skuViews)) return out;
    for (const v of skuViews) {
        if (!v || typeof v.code !== 'string') continue;
        const isFront =
            v.group != null ? _isFrontGroup(v.group) : !_looksBack(v.code);
        if (isFront) out.add(v.code);
    }
    return out;
}

/**
 * The set of view codes for a SKU's back-of-garment group. Symmetric to
 * frontGroupCodesForSku (empty set when no live views).
 *
 * @param {Array<{code: string, group?: string}>|null|undefined} skuViews
 * @returns {Set<string>}
 */
export function backGroupCodesForSku(skuViews) {
    const out = new Set();
    if (!Array.isArray(skuViews)) return out;
    for (const v of skuViews) {
        if (!v || typeof v.code !== 'string') continue;
        const isBack =
            v.group != null ? !_isFrontGroup(v.group) : _looksBack(v.code);
        if (isBack) out.add(v.code);
    }
    return out;
}
